import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LuClipboardList, LuPlus, LuSend } from 'react-icons/lu';
import AuthContext from '../../components/shared/AuthContext';
import TeamAssignmentCard from '../../components/teams/TeamAssignmentCard';
import { subscribeRealtimeInvalidate } from '../../realtime/realtimeBus';

export default function TeamAssignmentsPage() {
  const { orgId } = useParams();
  const { apiClient } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [createdByMe, setCreatedByMe] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [autoExpanded, setAutoExpanded] = useState(false);
  const [groups, setGroups] = useState([]);
  const [title, setTitle] = useState('');
  const [targetCount, setTargetCount] = useState(20);
  const [notebookTopic, setNotebookTopic] = useState('');
  const [filterKind, setFilterKind] = useState('any');
  const [categories, setCategories] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topicsInNotebook, setTopicsInNotebook] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [topicInNotebook, setTopicInNotebook] = useState('');
  const [assigneeType, setAssigneeType] = useState('GROUP');
  const [assigneeGroupId, setAssigneeGroupId] = useState('');
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const load = () => apiClient.get(`/api/organizations/${orgId}/assignments`).then((r) => {
    const data = r.data;
    if (Array.isArray(data)) {
      setItems(data);
      setCreatedByMe([]);
      return;
    }
    setItems(Array.isArray(data?.assignments) ? data.assignments : []);
    setCreatedByMe(Array.isArray(data?.createdByMe) ? data.createdByMe : []);
  });

  useEffect(() => {
    load().catch(() => setError('Could not load assignments.'));
    apiClient.get(`/api/organizations/${orgId}/groups`).then((r) => setGroups(r.data || [])).catch(() => {});

    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/api/get-filters');
        if (cancelled) return;
        setCategories(Array.isArray(res.data?.category) ? res.data.category.map(String) : []);
        setTopics(Array.isArray(res.data?.topic) ? res.data.topic.map(String) : []);
      } catch {
        if (!cancelled) setError((prev) => prev || 'Could not load library notebooks/topics.');
      } finally {
        if (!cancelled) setFiltersLoading(false);
      }
    })();

    const refreshList = () => {
      if (document.visibilityState === 'visible') load().catch(() => {});
    };
    window.addEventListener('focus', refreshList);
    const interval = window.setInterval(refreshList, 90000);
    document.addEventListener('visibilitychange', refreshList);
    const unsubRealtime = subscribeRealtimeInvalidate((detail) => {
      if (detail?.reason === 'read-toggle' || detail?.reason === 'team-assignment-progress') {
        load().catch(() => {});
      }
    });
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refreshList);
      document.removeEventListener('visibilitychange', refreshList);
      window.clearInterval(interval);
      unsubRealtime();
    };
  }, [apiClient, orgId]);

  useEffect(() => {
    if (autoExpanded || items.length === 0) return;
    const firstOpen = items.find((a) => a.status === 'OPEN');
    if (firstOpen?.id) {
      setExpandedId(firstOpen.id);
      setAutoExpanded(true);
    }
  }, [items, autoExpanded]);

  useEffect(() => {
    if (filterKind !== 'notebook' || !notebookTopic) {
      setTopicsInNotebook([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/api/get-note-topic?key=${encodeURIComponent(notebookTopic)}`);
        if (!cancelled) {
          setTopicsInNotebook(Array.isArray(res.data) ? res.data.map(String) : []);
        }
      } catch {
        if (!cancelled) setTopicsInNotebook([]);
      }
    })();
    return () => { cancelled = true; };
  }, [apiClient, filterKind, notebookTopic]);

  const resolvedFilterValue = () => {
    if (filterKind === 'any' || !notebookTopic) return undefined;
    if (filterKind === 'topic') return notebookTopic.trim();
    if (topicInNotebook) return topicInNotebook.trim();
    return notebookTopic.trim();
  };

  const globalError = error && (
    <div className="teams-card">
      <div className="teams-error" role="alert">{error}</div>
    </div>
  );

  const create = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await apiClient.post(`/api/organizations/${orgId}/assignments`, {
        title,
        assigneeType,
        assigneeGroupId: assigneeType === 'GROUP' ? Number(assigneeGroupId) : undefined,
        notebookTopic: resolvedFilterValue(),
        targetCount: Number(targetCount),
      });
      setTitle('');
      setNotebookTopic('');
      setTopicInNotebook('');
      setFilterKind('any');
      await load();
      setShowCreateForm(false);
      if (res.data?.id) setExpandedId(res.data.id);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.detail || 'Could not create assignment.');
    }
  };

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const deleteAssignment = async (assignmentId) => {
    setError('');
    try {
      await apiClient.delete(`/api/organizations/${orgId}/assignments/${assignmentId}`);
      if (expandedId === assignmentId) setExpandedId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.detail || 'Could not delete assignment.');
    }
  };

  const renderAssignmentList = (list) => (
    <div className="teams-assignment-list">
      {list.map((a) => (
        <TeamAssignmentCard
          key={a.id}
          orgId={orgId}
          apiClient={apiClient}
          summary={a}
          expanded={expandedId === a.id}
          onToggle={() => toggleExpanded(a.id)}
          onDelete={a.canDelete ? deleteAssignment : undefined}
        />
      ))}
    </div>
  );

  return (
    <>
      {globalError}
      {showCreateForm && (
      <div className="teams-card">
        <h2>New assignment</h2>
        <p className="teams-sub">
          Assignees receive a request notification and must accept before the assignment appears on their list.
          Progress counts after they accept and mark questions <strong>read</strong> in Library.
        </p>
        <form onSubmit={create}>
          <div className="teams-form-row">
            <label htmlFor="a-title">Title</label>
            <input id="a-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="teams-form-row">
            <label htmlFor="a-filter-kind">Content filter (optional)</label>
            <select
              id="a-filter-kind"
              value={filterKind}
              onChange={(e) => {
                setFilterKind(e.target.value);
                setNotebookTopic('');
                setTopicInNotebook('');
              }}
              disabled={filtersLoading}
            >
              <option value="any">Any library content</option>
              <option value="notebook">Notebook (category)</option>
              <option value="topic">Topic (any notebook)</option>
            </select>
          </div>
          {filterKind === 'notebook' && (
            <>
              <div className="teams-form-row">
                <label htmlFor="a-notebook">Notebook</label>
                <select
                  id="a-notebook"
                  value={notebookTopic}
                  onChange={(e) => {
                    setNotebookTopic(e.target.value);
                    setTopicInNotebook('');
                  }}
                  disabled={filtersLoading}
                >
                  <option value="">{filtersLoading ? 'Loading…' : 'Select notebook…'}</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              {notebookTopic && topicsInNotebook.length > 0 && (
                <div className="teams-form-row">
                  <label htmlFor="a-notebook-topic">Topic within notebook (optional)</label>
                  <select
                    id="a-notebook-topic"
                    value={topicInNotebook}
                    onChange={(e) => setTopicInNotebook(e.target.value)}
                  >
                    <option value="">All topics in this notebook</option>
                    {topicsInNotebook.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
          {filterKind === 'topic' && (
            <div className="teams-form-row">
              <label htmlFor="a-topic">Topic</label>
              <select
                id="a-topic"
                value={notebookTopic}
                onChange={(e) => setNotebookTopic(e.target.value)}
                disabled={filtersLoading}
              >
                <option value="">{filtersLoading ? 'Loading…' : 'Select topic…'}</option>
                {topics.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          )}
          <div className="teams-form-row">
            <label htmlFor="a-target">Target questions per assignee</label>
            <input id="a-target" type="number" min={1} value={targetCount} onChange={(e) => setTargetCount(e.target.value)} />
          </div>
          <div className="teams-form-row">
            <label htmlFor="a-type">Assign to</label>
            <select id="a-type" value={assigneeType} onChange={(e) => setAssigneeType(e.target.value)}>
              <option value="GROUP">Group</option>
              <option value="TEAM">Entire team</option>
            </select>
          </div>
          {assigneeType === 'GROUP' && (
            <div className="teams-form-row">
              <label htmlFor="a-group">Group</label>
              <select id="a-group" value={assigneeGroupId} onChange={(e) => setAssigneeGroupId(e.target.value)} required>
                <option value="">Select group…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({g.memberCount ?? 0} members)
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="teams-form-actions">
            <button type="button" className="teams-btn teams-btn--secondary" onClick={() => setShowCreateForm(false)}>
              Cancel
            </button>
            <button type="submit" className="teams-btn">
              <LuPlus aria-hidden />
              Create assignment
            </button>
          </div>
        </form>
      </div>
      )}

      <div className="teams-card">
        <div className="teams-page-toolbar">
          <div>
            <h2 className="teams-section-title">
              <LuClipboardList aria-hidden />
              Your assignments
            </h2>
            <p className="teams-sub">
              Assignments you accepted. Read marks count toward progress only after you accept the invite.
            </p>
          </div>
          <button type="button" className="teams-btn" onClick={() => setShowCreateForm(true)}>
            <LuPlus aria-hidden />
            New assignment
          </button>
        </div>
        {items.length === 0 ? (
          <p className="teams-sub">No accepted assignments yet. Check Requests to accept invites.</p>
        ) : (
          renderAssignmentList(items)
        )}
      </div>

      <div className="teams-card">
        <h2 className="teams-section-title">
          <LuSend aria-hidden />
          Assignments requested by you
        </h2>
        <p className="teams-sub teams-section-lead">
          Assignments you created. You can delete any you no longer need; assignees must still accept to start progress.
        </p>
        {createdByMe.length === 0 ? (
          <p className="teams-sub">You have not created any assignments yet.</p>
        ) : (
          renderAssignmentList(createdByMe)
        )}
      </div>
    </>
  );
}
